/*
  Session state for the console.

  This provider answers exactly one question for the router: is there a signed in
  technician, and who are they. It does NOT hold the access token. The token lives in
  src/lib/api/token-store.ts and this provider reacts to it being cleared.

  Bootstrap sequence on a cold load:

    1. No access token exists, because it was only ever in memory and the page reloaded.
    2. Call the refresh endpoint. The browser attaches the HttpOnly refresh cookie.
    3. If that works, fetch /auth/me and render the app. If it does not, render the login
       page.

  Step 2 is why the first paint shows a shell-shaped loading state rather than the login
  form: bouncing a signed in technician to a login screen for 200 milliseconds on every
  reload is the visible cost of getting token storage right, and it is avoidable.

  The permission list on CurrentUser drives UI affordances only. Every gate here is a
  convenience, never a control: docs/07-identity-rbac.md I.3 is explicit that the UI is
  never consulted and a hand-crafted request gets the same answer from the server.
*/

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { refreshAccessToken } from '@/lib/api/client';
import { isMockTransport } from '@/lib/api/config';
import { fetchCurrentUser, signOut as signOutRequest } from '@/lib/api/endpoints/auth';
import {
  getAccessToken,
  subscribeToAccessToken,
  type AccessToken,
} from '@/lib/api/token-store';
import type { CurrentUser, Permission } from '@/lib/api/types';

export type AuthStatus = 'bootstrapping' | 'signed_out' | 'signed_in';

export interface AuthState {
  readonly status: AuthStatus;
  readonly user: CurrentUser | null;
  /** Called by the login flow once a token is in the in-memory store. */
  readonly onSignedIn: () => Promise<void>;
  readonly signOut: () => Promise<void>;
  /** UI affordance check only. Server side enforcement is independent. */
  readonly can: (permission: Permission) => boolean;
  readonly canAny: (permissions: readonly Permission[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { readonly children: React.ReactNode }): React.JSX.Element {
  const [status, setStatus] = useState<AuthStatus>('bootstrapping');
  const [user, setUser] = useState<CurrentUser | null>(null);
  const mock = isMockTransport();

  const loadUser = useCallback(async (): Promise<void> => {
    try {
      const nextUser = await fetchCurrentUser();
      setUser(nextUser);
      setStatus('signed_in');
    } catch {
      setUser(null);
      setStatus('signed_out');
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap(): Promise<void> {
      // Under the mock transport there is no refresh cookie to exchange, so a reload always
      // lands on the login page. That is the honest behaviour for fixtures: the real
      // cookie exchange is the only thing that can restore a session.
      const token: AccessToken | null = mock ? getAccessToken() : await refreshAccessToken();
      if (cancelled) {
        return;
      }
      if (token === null) {
        setStatus('signed_out');
        return;
      }
      await loadUser();
    }

    void bootstrap();
    return () => {
      cancelled = true;
    };
  }, [loadUser, mock]);

  useEffect(() =>
    subscribeToAccessToken((token) => {
      // A refresh failure anywhere in the app clears the token. That is a forced sign out
      // and the router has to see it immediately.
      if (token === null) {
        setUser(null);
        setStatus((current) => (current === 'bootstrapping' ? current : 'signed_out'));
      }
    }),
  []);

  const signOut = useCallback(async (): Promise<void> => {
    await signOutRequest();
    setUser(null);
    setStatus('signed_out');
  }, []);

  const value = useMemo<AuthState>(() => {
    const permissions = new Set<Permission>(user?.permissions ?? []);
    return {
      status,
      user,
      onSignedIn: loadUser,
      signOut,
      can: (permission) => permissions.has(permission),
      canAny: (wanted) => wanted.some((permission) => permissions.has(permission)),
    };
  }, [loadUser, signOut, status, user]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
